import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import StatusBadge from '../../../components/status/StatusBadge';
import { TRANSACTION_TYPES_LABELS } from '../../../constants/transactionTypes';
import { ArrowRightCircle, Archive, ChevronUp, ChevronDown, RotateCcw, FolderX, Briefcase } from 'lucide-react';
import { updateTransaction } from '../api/transactionsAPI';
import { useNotification } from '../../../context/NotificationContext';
import { useModal } from '../../../context/ModalContext';
import PaginationFooter from '../../../components/ui/PaginationFooter';
import { getPaginationState } from '../../../utils/pagination';
import Tooltip from '../../../components/ui/Tooltip';

const ROWS_PER_PAGE = 10;

export default function TransactionsTable({ transactions = [], loading = false, onRefresh, isArchivedView = false, paginationResetKey }) {
  const { showToast } = useNotification();
  const { confirm } = useModal();
  const [sortConfig, setSortConfig] = useState({ key: 'title', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  const sortedTransactions = useMemo(() => {
    let sortableItems = [...transactions];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'client') {
          aValue = a.famille?.nom || a.societe?.denomination || '';
          bValue = b.famille?.nom || b.societe?.denomination || '';
        } else if (sortConfig.key === 'type') {
          aValue = TRANSACTION_TYPES_LABELS[a.transactionType] || a.transactionType;
          bValue = TRANSACTION_TYPES_LABELS[b.transactionType] || b.transactionType;
        }

        if (aValue == null) return 1;
        if (bValue == null) return -1;

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [transactions, sortConfig]);

  const { safePage, startIndex, endIndex } = getPaginationState({
    page: currentPage,
    pageSize: ROWS_PER_PAGE,
    totalItems: sortedTransactions.length,
  });
  const currentData = sortedTransactions.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [paginationResetKey, isArchivedView]);

  useEffect(() => {
    setCurrentPage((page) => getPaginationState({
      page,
      pageSize: ROWS_PER_PAGE,
      totalItems: sortedTransactions.length,
    }).safePage);
  }, [sortedTransactions.length]);

  const handleArchive = async (id) => {
    const isConfirmed = await confirm({
      title: "Clôturer la transaction",
      message: "Voulez-vous vraiment clôturer et archiver cette transaction ?",
      type: "danger",
      confirmText: "Archiver",
      cancelText: "Annuler"
    });
    if (!isConfirmed) return;
    try {
      await updateTransaction(id, { status: 'COMPLETED' });
      showToast("Transaction archivée avec succès", "success");
      if (onRefresh) onRefresh();
    } catch (error) {
      showToast("Erreur lors de l'archivage de la transaction", "error");
    }
  };

  const handleUnarchive = async (id) => {
    const isConfirmed = await confirm({
      title: "Réouvrir la transaction",
      message: "Voulez-vous réouvrir cette transaction et la replacer en cours d'opération ?",
      type: "info",
      confirmText: "Réouvrir",
      cancelText: "Annuler"
    });
    if (!isConfirmed) return;
    try {
      await updateTransaction(id, { status: 'ACTIVE' });
      showToast("Transaction réouverte avec succès", "success");
      if (onRefresh) onRefresh();
    } catch (error) {
      showToast("Erreur lors de la réouverture de la transaction", "error");
    }
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' 
        ? <ChevronUp className="inline w-3.5 h-3.5 ml-1 text-primary" /> 
        : <ChevronDown className="inline w-3.5 h-3.5 ml-1 text-primary" />;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-[var(--hp-color-border-muted)] bg-surface p-12 text-center shadow-[var(--hp-shadow-sm)]">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-surface-muted">
          <Briefcase className="h-6 w-6 animate-pulse text-tertiary" />
        </div>
        <p className="mt-3 text-sm font-medium text-subtle">Chargement des transactions...</p>
      </div>
    );
  }

  if (!transactions.length) {
    return (
      <div className="rounded-xl border border-[var(--hp-color-border-muted)] bg-surface p-12 text-center shadow-[var(--hp-shadow-sm)]">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-surface-muted">
          <FolderX className="h-6 w-6 text-tertiary" />
        </div>
        <h3 className="mt-3 text-base font-semibold text-primary">
          {isArchivedView ? "Aucune transaction archivée" : "Aucune transaction en cours"}
        </h3>
        <p className="mt-1 text-sm text-subtle">
          {isArchivedView 
            ? "Aucune opération n'a encore été clôturée ou archivée." 
            : "Aucune transaction ne correspond à vos critères de recherche actuels."}
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-[var(--hp-color-border-muted)] bg-surface shadow-[var(--hp-shadow-sm)]">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm border-collapse">
          <thead>
            <tr className="border-b border-[var(--hp-color-border-muted)] bg-surface-muted/60 text-xs font-semibold uppercase tracking-wider text-subtle">
              <th onClick={() => handleSort('title')} className="px-5 py-3.5 cursor-pointer hover:bg-surface-muted transition-colors select-none">
                <div className="flex items-center">
                  <span>Référence & Titre</span>
                  {renderSortIcon('title')}
                </div>
              </th>
              <th onClick={() => handleSort('type')} className="px-5 py-3.5 cursor-pointer hover:bg-surface-muted transition-colors select-none">
                <div className="flex items-center">
                  <span>Type</span>
                  {renderSortIcon('type')}
                </div>
              </th>
              <th onClick={() => handleSort('status')} className="px-5 py-3.5 cursor-pointer hover:bg-surface-muted transition-colors select-none">
                <div className="flex items-center">
                  <span>Statut</span>
                  {renderSortIcon('status')}
                </div>
              </th>
              <th onClick={() => handleSort('client')} className="px-5 py-3.5 cursor-pointer hover:bg-surface-muted transition-colors select-none">
                <div className="flex items-center">
                  <span>Client Principal</span>
                  {renderSortIcon('client')}
                </div>
              </th>
              <th onClick={() => handleSort('targetAmount')} className="px-5 py-3.5 cursor-pointer hover:bg-surface-muted transition-colors select-none">
                <div className="flex items-center">
                  <span>Montant Cible</span>
                  {renderSortIcon('targetAmount')}
                </div>
              </th>
              <th className="px-5 py-3.5 text-right font-medium">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--hp-color-border-muted)]">
            {currentData.map((tx) => (
              <tr key={tx.id} className="group hover:bg-surface-muted/50 transition-colors duration-150">
                {/* Titre & Ref */}
                <td className="px-5 py-4">
                  <div className="flex flex-col gap-0.5">
                    <Link 
                      to={`/transactions/${tx.id}`} 
                      className="font-semibold text-primary hover:text-secondary transition-colors truncate max-w-xs sm:max-w-sm"
                      title={tx.title}
                    >
                      {tx.title}
                    </Link>
                    <span className="inline-flex items-center gap-1 font-mono text-xs text-subtle">
                      #{tx.reference}
                    </span>
                  </div>
                </td>

                {/* Type */}
                <td className="px-5 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center rounded-md bg-surface-muted px-2.5 py-1 text-xs font-medium text-primary border border-[var(--hp-color-border-muted)]">
                    {TRANSACTION_TYPES_LABELS[tx.transactionType] || tx.transactionType}
                  </span>
                </td>

                {/* Statut */}
                <td className="px-5 py-4 whitespace-nowrap">
                  <StatusBadge status={tx.status} type="transaction" />
                </td>

                {/* Client */}
                <td className="px-5 py-4 whitespace-nowrap">
                  <span className="text-sm text-primary font-medium">
                    {tx.famille?.nom 
                      ? `Famille ${tx.famille.nom}` 
                      : tx.societe?.denomination 
                        ? tx.societe.denomination 
                        : <span className="text-subtle italic">Non renseigné</span>}
                  </span>
                </td>

                {/* Objectif */}
                <td className="px-5 py-4 whitespace-nowrap font-medium text-primary">
                  {tx.targetAmount 
                    ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: tx.currency || 'EUR', maximumFractionDigits: 0 }).format(tx.targetAmount) 
                    : <span className="text-subtle">N/C</span>}
                </td>

                {/* Actions */}
                <td className="px-5 py-4 whitespace-nowrap text-right">
                  <div className="inline-flex items-center justify-end gap-3">
                    <Tooltip content="Consulter / Piloter">
                      <Link 
                        to={`/transactions/${tx.id}`} 
                        className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-secondary hover:bg-secondary-50 transition-colors"
                      >
                        <ArrowRightCircle className="h-4 w-4" />
                        <span>Piloter</span>
                      </Link>
                    </Tooltip>

                    {isArchivedView ? (
                      <Tooltip content="Réouvrir l'opération">
                        <button 
                          onClick={() => handleUnarchive(tx.id)}
                          className="inline-flex items-center rounded-lg p-1.5 text-subtle hover:bg-surface-muted hover:text-primary transition-colors"
                        >
                          <RotateCcw className="h-4 w-4" />
                        </button>
                      </Tooltip>
                    ) : (
                      <Tooltip content="Clôturer & Archiver">
                        <button 
                          onClick={() => handleArchive(tx.id)}
                          className="inline-flex items-center rounded-lg p-1.5 text-subtle hover:bg-[var(--hp-danger-50)] hover:text-[var(--hp-danger-600)] transition-colors"
                        >
                          <Archive className="h-4 w-4" />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <PaginationFooter
        page={safePage}
        pageSize={ROWS_PER_PAGE}
        totalItems={sortedTransactions.length}
        onPageChange={setCurrentPage}
        ariaLabel="Pagination des transactions"
      />
    </div>
  );
}
